import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, LinkIcon } from "lucide-react";
import { KanjiComponents } from "~/components/kanji-components";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import {
	useGenerateAIExample,
	useGenerateMassifExample,
	useVocabularyItem,
	useVocabularySentenceExamples,
	vocabularyItemQueryOptions,
} from "~/lib/client/vocabulary";
import { useSetAtom } from "jotai";
import {
	currentPageContextAtom,
	type ChatWidgetContext,
} from "~/components/practice/chat-widget";
import type { VocabularyContent } from "~/lib/server/db/schema/flashcard-schema";
import { m } from "~/i18n/messages";

function VocabularyItemSkeleton() {
	return (
		<div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 w-full max-w-screen-lg mx-auto px-3 sm:px-4 pb-6">
			<div className="lg:col-span-2">
				{/* Back button skeleton */}
				<div className="flex items-center gap-1 py-3">
					<Skeleton className="w-4 h-4" />
					<Skeleton className="w-20 h-4" />
				</div>
			</div>
			<div className="space-y-3 sm:space-y-4">
				{/* Header Section Skeleton */}
				<div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
					<div className="flex items-center justify-between">
						<div>
							<Skeleton className="h-10 w-32 mb-2" />
							<Skeleton className="h-5 w-24" />
						</div>
						<Skeleton className="h-6 w-12 rounded-full" />
					</div>
				</div>

				<div className="space-y-3 sm:space-y-4">
					{/* Meanings Section Skeleton */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<Skeleton className="h-6 w-20 mb-2" />
						<div className="space-y-2">
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-4 w-3/4" />
							<Skeleton className="h-4 w-5/6" />
						</div>
					</div>

					{/* Examples Section Skeleton */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<div className="flex items-center justify-between mb-2">
							<Skeleton className="h-6 w-32" />
							<Skeleton className="h-4 w-16" />
						</div>
						<div className="space-y-4">
							{Array.from({ length: 3 }, (_, i) => `example-${i}`).map((key) => (
								<div key={key} className="group">
									<div className="flex items-start sm:items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
										<Skeleton className="h-4 w-full" />
										<Skeleton className="h-6 w-16 rounded-full" />
									</div>
									<Skeleton className="h-3 w-3/4 mt-1 ml-4" />
								</div>
							))}
						</div>
						
						{/* Generation Buttons Skeleton */}
						<div className="space-y-3 mt-4 sm:mt-6 pt-4 border-t border-pink-100">
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-4 w-full" />
						</div>
					</div>
				</div>
			</div>
			<div>
				<div className="space-y-3 sm:space-y-4 sticky top-20">
					{/* Kanji Components Skeleton */}
					<div className="bg-white rounded-lg p-4 shadow-sm border border-pink-200">
						<Skeleton className="h-6 w-24 mb-3" />
						<div className="grid grid-cols-2 gap-2">
							{Array.from({ length: 4 }, (_, i) => `kanji-${i}`).map((key) => (
								<Skeleton key={key} className="h-16 w-full" />
							))}
						</div>
					</div>
					
					{/* External Links Skeleton */}
					<div className="flex flex-col gap-2">
						{Array.from({ length: 4 }, (_, i) => `link-${i}`).map((key) => (
							<Skeleton key={key} className="h-10 w-full" />
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute(
	"/app/learn/jlpt/$level/vocabulary/$vocabId",
)({
	component: () => (
		<SuspenseFallback
			loading={<VocabularyItemSkeleton />}
			error={
				<div className="flex justify-center items-center min-h-[400px]">
					<div className="text-center">
						<h2 className="text-xl font-semibold text-red-600 mb-2">
							{m["errors.something_went_wrong"]()}
						</h2>
						<p className="text-gray-600">
							{m["errors.vocabulary_load_failed"]()}
						</p>
					</div>
				</div>
			}
		>
			<VocabularyItemComponent />
		</SuspenseFallback>
	),
	loader: async ({ context, params }) => {
		const data = await context.queryClient.ensureQueryData(
			vocabularyItemQueryOptions(params.vocabId),
		);
		// TODO: Handle potential changes in vocabulary data structure here
		console.log("Fetched vocabulary data:", data); // Placeholder for handling data structure changes
	},
});

function VocabularyItemComponent() {
	const { vocabId, level } = Route.useParams();
	const setPageContext = useSetAtom(currentPageContextAtom);

	const { data: vocab } = useVocabularyItem(vocabId);
	const content = vocab?.content as VocabularyContent | undefined;

	useEffect(() => {
		if (vocab && content) {
			const context: ChatWidgetContext = {
				title: content.kanji?.[0]?.kanji ?? vocab.reading,
				slug: `/app/learn/jlpt/${level}/vocabulary/${vocabId}`,
				contextData: {
					id: vocabId,
					level: vocab.level,
					readings: content.readings,
					translations: content.translations,
					kanji: content.kanji,
				},
			};
			setPageContext(context);
		}
	}, [vocab, vocabId, content, setPageContext, level]);

	const { data: examples } = useVocabularySentenceExamples(vocab ?? null);

	const [showAllTranslations, setShowAllTranslations] = useState(false);
	const generateAIExample = useGenerateAIExample();
	const generateMassifExample = useGenerateMassifExample();

	const isGenerating =
		generateAIExample.isPending || generateMassifExample.isPending;

	if (!vocab || !("readings" in vocab.content)) {
		return <div className="p-8 text-red-500">Vocabulary not found</div>;
	}

	const allExamples = examples || [];

	return (
		<div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 w-full max-w-screen-lg mx-auto px-3 sm:px-4 pb-6">
			<div className="lg:col-span-2">
				<Link
					to="/app/learn/jlpt/$level"
					params={{ level }}
					className="flex items-center gap-1 text-pink-500/80 hover:text-pink-600 transition-colors text-sm py-3"
				>
					<ChevronLeft className="w-4 h-4" />
					<span>{m["ui.back_to_list_button"]()}</span>
				</Link>
			</div>
			<div className="space-y-3 sm:space-y-4">
				{/* Header Section */}
				<div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
					<div className="flex items-center justify-between">
						<div>
							<h1 className="text-3xl sm:text-4xl font-bold text-pink-800">
								{vocab.content.kanji?.[0]?.kanji ?? vocab.reading}
							</h1>
							<p className="text-pink-600/80 mt-2 text-sm sm:text-base">
								{vocab.content.readings?.join(", ")}
							</p>
						</div>
						<span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-xs font-medium">
							{vocab.level.toUpperCase()}
						</span>
					</div>
				</div>

				<div className="space-y-3 sm:space-y-4">
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<h2 className="text-lg sm:text-xl font-semibold text-pink-700 mb-2">
							{m["ui.meanings_label"]()}
						</h2>
						<ul className="space-y-2">
							{(showAllTranslations
								? vocab.content.translations
								: vocab.content.translations.slice(0, 3)
							).map((t) => {
								const notes = t.notes ?? [];
								return (
									<li
										key={t.translation}
										className="text-pink-800 text-sm sm:text-base"
									>
										• {t.translation.trim()}
										{notes.length > 0 && (
											<p className="text-pink-600/70 text-xs sm:text-sm ml-4">
												{notes.join("; ")}
											</p>
										)}
									</li>
								);
							})}
						</ul>
						{vocab.content.translations.length > 3 && (
							<Button
								variant="link"
								className="text-pink-600 mt-2 px-0 text-sm"
								onClick={() => setShowAllTranslations(!showAllTranslations)}
							>
								{showAllTranslations
									? m["ui.show_less_button"]()
									: m["ui.show_all_count_button"]({
											count: vocab.content.translations.length,
										})}
							</Button>
						)}
					</div>

					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<div className="flex items-center justify-between mb-2">
							<h2 className="text-lg sm:text-xl font-semibold text-pink-700">
								{m["dictionary.example_sentences.title"]()}
							</h2>
							<span className="text-xs text-muted-foreground">
								{m["dictionary.example_sentences.source_label"]()}
							</span>
						</div>
						{allExamples.length > 0 ? (
							<div className="space-y-4">
								{allExamples.map((p) => (
									<div key={p.sentence} className="group">
										<div className="flex items-start sm:items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
											<p
												className="text-pink-800 font-normal text-sm sm:text-base [&_em]:text-pink-500 [&_em]:font-semibold [&_em]:not-italic [&_em]:underline"
												// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
												dangerouslySetInnerHTML={{
													__html:
														p.sentence?.replaceAll(
															vocab.content.kanji?.[0]?.kanji ?? vocab.reading,
															(match: string) => `<em>${match}</em>`,
														) ?? "",
												}}
											/>
											{p.source !== "AI" ? (
												<a
													href={p.source}
													className="flex gap-1 items-center px-2 rounded-full text-xs text-pink-600/70 bg-pink-50 border border-pink-300 mt-1 sm:mt-0 flex-shrink-0"
													target="_blank"
													rel="noreferrer"
												>
													{m["ui.open_link_button"]()}
													<LinkIcon className="size-3" />
												</a>
											) : (
												<div className="flex gap-1 items-center px-2 rounded-full text-xs text-pink-600/70 bg-pink-50 border border-pink-300 mt-1 sm:mt-0 flex-shrink-0">
													{m["ui.ai_label"]()}
												</div>
											)}
										</div>
										<ul className="text-rose-800/50 text-xs sm:text-sm mt-1 pl-4">
											{p.translation}
										</ul>
									</div>
								))}
							</div>
						) : (
							<p className="text-pink-600/80 text-sm">
								{m["dictionary.example_sentences.none_available_message"]()}
							</p>
						)}

						{/* Generation Buttons and Errors */}
						<div className="space-y-3 mt-4 sm:mt-6 pt-4 border-t border-pink-100">
							{/* AI Generation Button */}
							<Button
								onClick={() =>
									generateAIExample.mutate({
										data: {
											vocabId,
											word: vocab.reading,
											readings: vocab.content.readings ?? [],
											meaning: {
												meaning:
													vocab.content.translations[0]?.translation || "",
												example:
													vocab.content.translations[0]?.notes?.join("; ") ||
													"",
											},
											level: vocab.level,
										},
									})
								}
								disabled={isGenerating}
								className="w-full text-sm"
								variant="outline"
							>
								<VocabularyButtonContent
									isPending={generateAIExample.isPending}
									defaultText={m[
										"dictionary.example_sentences.generate_ai_button"
									]()}
								/>
							</Button>

							{/* Massif Generation Button */}
							<Button
								onClick={() =>
									generateMassifExample.mutate({
										data: {
											vocabId,
											word: vocab.reading,
											count: 3,
										},
									})
								}
								disabled={isGenerating}
								className="w-full text-sm"
							>
								<VocabularyButtonContent
									isPending={generateMassifExample.isPending}
									defaultText={m[
										"dictionary.example_sentences.generate_massif_button"
									]()}
								/>
							</Button>

							{/* AI Error Display */}
							{generateAIExample.error && (
								<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs sm:text-sm">
									{m["errors.ai_error_prefix"]()}{" "}
									{generateAIExample.error.message}
								</div>
							)}

							{/* Massif Error Display */}
							{generateMassifExample.error && (
								<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs sm:text-sm">
									{m["errors.massif_error_prefix"]()}{" "}
									{generateMassifExample.error.message}
								</div>
							)}

							{/* General Note */}
							<p className="text-xs text-pink-600/70 pt-2">
								{m["dictionary.example_sentences.accuracy_note_text"]()}
								with other resources. Massif examples come from a large corpus
								and may be more reliable.
							</p>
						</div>
					</div>
				</div>
			</div>
			<div>
				<div className="space-y-3 sm:space-y-4 sticky top-20">
					{/* Kanji Breakdown */}
					{vocab.content.kanji && (
						<KanjiComponents
							reading={vocab.reading}
							kanji={vocab.content.kanji}
						/>
					)}
					<div className="flex flex-col gap-2">
						<Button
							asChild
							variant="ghost"
							className="w-full bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 text-xs sm:text-sm"
						>
							<a
								href={`https://jisho.org/search/${vocab.reading}`}
								target="_blank"
								rel="noreferrer"
								className="w-full"
							>
								{vocab.reading} in {m["dictionary.external_links.jisho"]()}
							</a>
						</Button>
						<Button
							asChild
							variant="ghost"
							className="w-full bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 text-xs sm:text-sm"
						>
							<a
								href={`https://ejje.weblio.jp/content/${vocab.reading}`}
								target="_blank"
								rel="noreferrer"
								className="w-full"
							>
								{vocab.reading} in Weblio
							</a>
						</Button>
						<Button
							asChild
							variant="ghost"
							className="w-full bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 text-xs sm:text-sm"
						>
							<a
								href={`https://dictionary.goo.ne.jp/srch/thsrs/${vocab.reading}/m0u/`}
								target="_blank"
								rel="noreferrer"
								className="w-full"
							>
								{vocab.reading} in Goo辞書
							</a>
						</Button>
						<Button
							asChild
							variant="ghost"
							className="w-full bg-pink-50 hover:bg-pink-100 text-pink-700 border border-pink-200 text-xs sm:text-sm"
						>
							<a
								href={`https://takoboto.jp/?q=${vocab.reading}`}
								target="_blank"
								rel="noreferrer"
								className="w-full"
							>
								{vocab.reading} in Takoboto
							</a>
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

type VocabularyButtonContentProps = {
	isPending: boolean;
	defaultText: string;
};

function VocabularyButtonContent(props: VocabularyButtonContentProps) {
	if (props.isPending) {
		return (
			<span className="flex items-center justify-center gap-2">
				<svg
					className="animate-spin h-4 w-4 text-white"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					aria-label="Loading"
				>
					<title>Loading spinner</title>
					<circle
						className="opacity-25"
						cx="12"
						cy="12"
						r="10"
						stroke="currentColor"
						strokeWidth="4"
					/>
					<path
						className="opacity-75"
						fill="currentColor"
						d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
					/>
				</svg>
				{m["ui.generating_label"]()}
			</span>
		);
	}
	return <>{props.defaultText}</>;
}
